// Third party
import React from 'react';

// Namespaces
import * as ProposalNS from '../../Proposal';

// Redux
import { ProposalTemplateOptions } from '@/redux/modules/types/proposal-v2';

// Components
import { CollapsibleContainer } from '@/views/components/misc';
import ContentPanelOption from './ContentPanelOption';

// Constants and types
import User from '@/types/user';

// Utils
import { formatLocalizedString, formatMessage } from '@/utils/i18n';
import { DropItemPosition } from '@/utils/drag-and-drop';
import { filterSectionsByType } from '../../utils/proposal';
import { runBuildRules } from '../../ProposalHelpers';

type Props = {
  user: User;
  proposalTemplateOptions: ProposalTemplateOptions;
  proposal: ProposalNS.Proposal;
  onJumpToPage: (section: string) => void;
  onToggleSection: (section: string, selected: boolean) => void;
  onToggleDivision: (division: string, selected: boolean) => void;
  onChangeItemOrder: (itemId: string, referenceId: string, position?: DropItemPosition) => void;
  onChangeContentOrder: (item: string, reference: string, position?: DropItemPosition) => void;
};

export default function ContentPanel(props: Props) {
  const { user, proposalTemplateOptions, proposal } = props;

  const removeOption = (option: ProposalNS.ProposalTemplateDivisionElement | ProposalNS.ProposalTemplateSectionElement) => {
    const { key } = option;

    if (option.type === ProposalNS.ProposalTemplateElementType.Division) {
      const selectedSections = proposalTemplateOptions?.sections.filter(selectedSection => option.sections.find(section => section.key === selectedSection));
      selectedSections.forEach(section => setTimeout(() => props.onToggleSection(section, false)));
    }

    if (option.type === ProposalNS.ProposalTemplateElementType.Section) {
      props.onToggleSection(key, false);
    }
  };

  const handleOnChangeContentOrder = (item: string, reference: string, position?: DropItemPosition) => {
    props.onChangeContentOrder(item, reference, position);
  };

  const renderSelections = () => {
    const {
      template: { root },
    } = proposal;
    const { sections } = proposalTemplateOptions;

    const rootSections = root.map(division => division.sections).flat(1);
    const coverSections = filterSectionsByType(rootSections, ProposalNS.ProposalSectionType.Cover);
    const disclaimerSections = filterSectionsByType(rootSections, ProposalNS.ProposalSectionType.Disclaimer);
    const customTopSections = filterSectionsByType(rootSections, ProposalNS.ProposalSectionType.CustomTop);
    const selectedCoverPages = sections.filter(section => coverSections.indexOf(section) > -1);
    const hasForcedCoverPages = rootSections.reduce((list: string[], currentItem) => {
      if (coverSections.indexOf(currentItem.key) !== -1 && !!currentItem.forced) list.push(currentItem.key);
      return list;
    }, []);

    let options = sections?.filter(
      section =>
        coverSections.indexOf(section) === -1 &&
        disclaimerSections.indexOf(section) === -1 &&
        rootSections.find(rootSection => {
          const { buildRules, forced, sidebarControl } = rootSection;

          if (buildRules && !runBuildRules(buildRules, proposal, user)) {
            return false;
          }

          const isHide = sidebarControl === ProposalNS.ProposalSidebarControl.Hide;
          const isNone = sidebarControl === ProposalNS.ProposalSidebarControl.None;
          return rootSection.key === section && ((forced && (isHide || isNone)) || (!forced && !isHide && !isNone));
        }),
    );

    if (selectedCoverPages.length) {
      options?.unshift(...selectedCoverPages);
    }

    if (!selectedCoverPages.length && hasForcedCoverPages.length) {
      options?.unshift(...hasForcedCoverPages);
    }

    if (disclaimerSections.length) {
      options?.push(disclaimerSections[0]);
    }

    options = options.filter((option, index, filteredOptions) => filteredOptions.indexOf(option) === index);

    return options?.map(option => {
      const selectedSection = rootSections.find(rootSection => {
        if (
          (rootSection.sidebarControl === ProposalNS.ProposalSidebarControl.Hide || rootSection.sidebarControl === ProposalNS.ProposalSidebarControl.None) &&
          !rootSection.forced &&
          disclaimerSections.indexOf(rootSection.key) === -1
        ) {
          return false;
        }
        return rootSection.key === option;
      });

      if (!selectedSection) {
        return null;
      }

      const isCover = coverSections.indexOf(selectedSection.key) !== -1;
      const isDisclaimer = disclaimerSections.indexOf(selectedSection.key) !== -1;
      const isCustomTop = customTopSections.indexOf(selectedSection.key) !== -1;
      const isLocked = isCover || isDisclaimer || isCustomTop || !!selectedSection.forced;

      return renderSection(selectedSection, isLocked);
    });
  };

  const renderSection = (element: ProposalNS.ProposalTemplateSectionElement, isLocked: boolean) => {
    const isDisabled = !!element.forced || element.sectionType === ProposalNS.ProposalSectionType.Disclaimer;
    let label = element.label ? formatLocalizedString(element.label) : '';

    if (element.sectionType === ProposalNS.ProposalSectionType.Disclaimer && !label) {
      label = formatMessage('Common.Disclaimer');
    }

    return (
      <ContentPanelOption
        key={element.key}
        element={element}
        label={label}
        disabled={isDisabled}
        locked={isLocked}
        onChangeContentOrder={handleOnChangeContentOrder}
        onRemoveOption={removeOption}
        onJumpToPage={props.onJumpToPage}
      />
    );
  };

  return (
    <div className="proposal-sidebar-section selected-sections">
      <CollapsibleContainer className="proposal-sections" headerText={formatMessage('Proposal.Tabs.Customize.Title')} headerTag="h2">
        {renderSelections()}
      </CollapsibleContainer>
    </div>
  );
}
